import type { ReactNode } from 'react';

/**
 * Direction A — "The Ledger". Platform Admin only; room-web and player-web run The Rail and
 * bring their own shells.
 *
 * Ink-green chrome down the left over a paper ground, square corners, hairline rules, no
 * shadow. A nav item marks itself with A's 3px active-rail, in --color-rail-accent: the
 * action green is too dark to read as an accent against the ink-green chrome, so the rail
 * gets its own lifted tone.
 *
 * Below lg the rail rotates to a bar across the top and the active-rail becomes an
 * underline, which is the same signal read the other way round.
 */

export interface AppShellNavItem<TView extends string> {
  id: TView;
  label: string;
}

export function AppShell<TView extends string = string>({
  appName,
  role,
  title,
  description,
  nav,
  actions,
  footer,
  children,
}: {
  appName: string;
  /** Sits under the brand in the rail — who this console is for. */
  role: string;
  /** The area currently on screen. */
  title: string;
  description?: string;
  nav?: {
    items: ReadonlyArray<AppShellNavItem<TView>>;
    current: TView;
    onNavigate: (id: TView) => void;
  };
  actions?: ReactNode;
  /**
   * Small print for the foot of the rail — the version line, today. Rendered in the rail from lg
   * up and under the content below it, mirroring how `actions` moves between the two: below lg
   * the rail is a bar across the top, where there is no foot to sit at.
   */
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-ground lg:flex">
      <aside className="flex flex-col bg-rail-900 lg:w-[212px] lg:shrink-0">
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <div className="min-w-0">
            <div className="truncate text-[13px] font-bold uppercase tracking-[0.08em] text-white">
              {appName}
            </div>
            <div className="mt-0.5 truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-rail-300">
              {role}
            </div>
          </div>
          {actions ? <div className="shrink-0 lg:hidden">{actions}</div> : null}
        </div>

        {nav ? (
          <nav
            className="flex overflow-x-auto lg:flex-col lg:overflow-visible"
            aria-label={`${appName} sections`}
          >
            {nav.items.map((item) => {
              const active = item.id === nav.current;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => nav.onNavigate(item.id)}
                  aria-current={active ? 'page' : undefined}
                  className={`flex min-h-[44px] shrink-0 items-center whitespace-nowrap border-b-[3px] border-transparent px-5 text-left text-[12.5px] transition lg:border-b-0 lg:border-l-[3px] lg:py-2.5 ${
                    active
                      ? 'border-b-rail-accent bg-white/[0.07] font-semibold text-white lg:border-l-rail-accent'
                      : 'text-rail-100 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>
        ) : null}

        {actions ? (
          <div className="mt-auto hidden border-t border-white/10 px-5 py-4 lg:block">{actions}</div>
        ) : null}

        {/* `mt-auto` here too: with no actions block above it, this is what pins the footer to the
            bottom of the rail rather than leaving it under the nav. */}
        {footer ? (
          <div className="mt-auto hidden border-t border-white/10 px-5 py-3 lg:block">{footer}</div>
        ) : null}
      </aside>

      <div className="min-w-0 flex-1">
        <header className="border-b border-line bg-surface px-6 py-5 sm:px-7">
          <h1 className="font-display text-xl font-semibold text-ink-950">{title}</h1>
          {description ? <p className="mt-1 text-sm text-ink-800">{description}</p> : null}
        </header>
        <main className="px-6 py-7 sm:px-7">{children}</main>
        {/* Colour is the footer's own: this ground is paper and the rail's is ink-green, so
            anything set here would be wrong in one of the two places it renders. */}
        {footer ? (
          <div className="border-t border-line px-6 py-4 sm:px-7 lg:hidden">{footer}</div>
        ) : null}
      </div>
    </div>
  );
}
